import { Avatar, AvatarSizes } from 'components/shared/avatar'
import { Presence } from 'components/shared/presence'
import type { PresenceProps } from 'components/shared/presence'
import React from 'react'

import { css } from 'design'
import { cn } from 'util/cn'

import { useHandleMetadata } from '../../hooks/use_handle_metadata'
import OwnerBadge from './owner_badge'

export type ContactSummaryProps = {
  participant: any
  isTeammate?: boolean
  variant?: 'list' | 'digest'
  presence: PresenceProps['variant']
} & React.HTMLAttributes<HTMLDivElement>

export const ContactSummary = ({
  participant,
  presence,
  isTeammate = false,
  variant = 'list',
  className,
  children,
  ...props
}: ContactSummaryProps) => {
  const handle = participant?.handle
  const { name, contact } = useHandleMetadata(handle, participant)

  return (
    <div
      className={cn(contactSummaryContainerStyles({ variant }), className)}
      {...props}
    >
      {handle && (
        <Avatar
          className={contactAvatarStyles()}
          handle={contact?.display_email || handle}
          size={variant === 'list' ? AvatarSizes.MEDIUM : AvatarSizes.MASSIVE}
        />
      )}

      <section className={contactDetailsStyles({ variant })}>
        <section
          className={cn(
            'contact-detail',
            contactDetailPrimaryStyles({ variant }),
            overflowStyles(),
          )}
        >
          <span
            className={cn(contactDetailsPrimaryNameStyles(), overflowStyles())}
          >
            {name}
          </span>

          {handle && <OwnerBadge handle={handle} />}

          {presence !== 'offline' && (
            <Presence variant={presence} size="small" />
          )}
        </section>
        {!isTeammate && (
          <section
            className={cn(
              contactDetailsSecondaryStyles({ variant }),
              overflowStyles(),
            )}
          >
            {name && (contact?.display_email || handle)}
          </section>
        )}
      </section>

      {children}
    </div>
  )
}

const contactSummaryContainerStyles = css({
  display: 'flex',
  alignItems: 'center',

  variants: {
    variant: {
      list: {
        width: '100%',
        justifyContent: 'space-between',
        '> * + *': {
          marginLeft: '$2',
        },
      },
      digest: {
        justifyContent: 'center',
        flexDirection: 'column',
        padding: '$10 0',
        '> * + *': {
          marginTop: '$5',
        },
      },
    },
  },
  defaultVariants: {
    variant: 'list',
  },
})

const contactDetailsStyles = css({
  display: 'flex',
  overflow: 'hidden',
  justifyContent: 'center',
  flexDirection: 'column',
  lineHeight: 1.3,

  variants: {
    variant: {
      list: {
        width: '100%',
        alignItems: 'flex-start',
      },
      digest: {
        width: '80%',
        alignItems: 'center',
      },
    },
  },
  defaultVariants: {
    variant: 'list',
  },
})

const contactAvatarStyles = css({
  pointerEvents: 'none',
})

const overflowStyles = css({
  textAlign: 'start',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const contactDetailPrimaryStyles = css({
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  color: '$grey900',
  fontSize: '$3',
  width: '100%',

  '> * + *': {
    marginLeft: '$2',
  },

  variants: {
    variant: {
      list: {
        justifyContent: 'flex-start',
      },
      digest: {
        justifyContent: 'center',
      },
    },
  },
  defaultVariants: {
    variant: 'list',
  },
})

const contactDetailsPrimaryNameStyles = css({
  display: 'inline-block',
  paddingRight: '$1',
})

const contactDetailsSecondaryStyles = css({
  color: '$grey500',
  fontSize: '$1',
  width: '100%',

  variants: {
    variant: {
      list: {
        textAlign: 'start',
      },
      digest: {
        textAlign: 'center',
      },
    },
  },
  defaultVariants: {
    variant: 'list',
  },
})
